import type { SentenceRecordingType } from 'types/types'
import { computed, reactive, watch, type WritableComputedRef } from 'vue'
import type { VueLogger } from 'vue-logger-plugin'

import { useAudioPlayer } from '@/composables/audioPlayer'
import store from '@/store'
import { DEFAULT_PITCH_SHIFT, PitchShiftType, SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'

export interface AudioPlaybackState {
  downloadingSentenceRecordingId: string | null
  pitchShift: PitchShiftType
  playingSentenceRecordingId: string | null
}

export function useAudioPlayback(logger: VueLogger) {
  const state = reactive<AudioPlaybackState>({
    downloadingSentenceRecordingId: null,
    pitchShift: DEFAULT_PITCH_SHIFT,
    playingSentenceRecordingId: null,
  })

  const { beginPlayback, isPlayable, pause, paused, play, playing, purge, setLoadPlay } = useAudioPlayer(logger)

  // Stop playback if the recording currently playing is deleted from the store (e.g. the user
  // deletes it mid-playback). We track "playing a recording that still exists" and react when it
  // flips false while playback is still active — i.e. the recording was removed out from under us.
  // A natural end clears playingSentenceRecordingId first, so the id guard skips that case. This
  // covers every delete path, since they all remove the recording from the audio store.
  watch(
    () => {
      const id = state.playingSentenceRecordingId
      return id !== null && store.getters.audio.recordingExists(id)
    },
    (playingExistingRecording) => {
      if (!playingExistingRecording && state.playingSentenceRecordingId !== null) {
        purge()
        state.playingSentenceRecordingId = null
      }
    },
  )

  // Shared: fetch audio, track playing state, run playback callback, cleanup
  const _withPlaybackTracking = async (metadata: SentenceRecordingMetadataWithPitchedAudios, playFn: () => Promise<void>) => {
    if (!metadata.sentenceRecording) {
      console.error('unexpected null sentenceRecording in playback')
      return
    }

    // SYNC: immediately stop old audio and mark as transitioning so `playing` is true
    // from the instant the user clicks — no gap during fetchAudio where the record button
    // could flash as enabled.
    beginPlayback()
    state.playingSentenceRecordingId = metadata.sentenceRecording.id
    console.log(`[useAudioPlayback._withPlaybackTracking] START, playing=${playing.value}, about to fetchAudio`)

    try {
      await store.dispatch.audio.fetchAudio({
        cache: true,
        pitchShift: state.pitchShift,
        sentenceRecording: metadata.sentenceRecording,
      })
      console.log(`[useAudioPlayback._withPlaybackTracking] fetchAudio done, playing=${playing.value}, about to call playFn`)
      await playFn()
      console.log(`[useAudioPlayback._withPlaybackTracking] playFn done, playing=${playing.value}`)
    } catch (e) {
      console.log(`[useAudioPlayback._withPlaybackTracking] CAUGHT: ${e}, playing=${playing.value}`)
    } finally {
      state.playingSentenceRecordingId = null
      console.log(`[useAudioPlayback._withPlaybackTracking] FINALLY, playing=${playing.value}`)
    }
  }

  const playSentenceOrSequencedSegments = (metadata: SentenceRecordingMetadataWithPitchedAudios) => {
    return _withPlaybackTracking(metadata, async () => {
      // Play segments until we can't — if this is a sentence, then this will run only once
      for (let segmentIndex = 0; ; segmentIndex += 1) {
        await setLoadPlay({
          pitchShift: state.pitchShift,
          segmentIndex,
          sentenceRecordingMetadataWithPitchedAudios: metadata,
        })
      }
    })
  }

  const playSegment = (metadata: SentenceRecordingMetadataWithPitchedAudios, segmentIndex: number) => {
    console.log(`[useAudioPlayback.playSegment] segmentIndex=${segmentIndex}, playing=${playing.value}`)
    return _withPlaybackTracking(metadata, () =>
      setLoadPlay({
        pitchShift: state.pitchShift,
        segmentIndex,
        sentenceRecordingMetadataWithPitchedAudios: metadata,
      }),
    )
  }

  const resume = (): void => {
    play().catch((err) => console.error('audio resume failed:', err))
  }

  const fetchRecordingForDownload = (sentenceRecordingMetadataWithPitchedAudios: SentenceRecordingMetadataWithPitchedAudios): void => {
    if (!sentenceRecordingMetadataWithPitchedAudios.sentenceRecording) {
      console.error('unexpected null sentenceRecordingMetadataWithPitchedAudios.sentenceRecording')
      return
    }

    state.downloadingSentenceRecordingId = sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.id

    store.dispatch.audio
      .fetchAudio({
        cache: true,
        pitchShift: DEFAULT_PITCH_SHIFT,
        sentenceRecording: sentenceRecordingMetadataWithPitchedAudios.sentenceRecording as SentenceRecordingType,
      })
      .then(() => {
        const link = document.createElement('a')
        if (!sentenceRecordingMetadataWithPitchedAudios.sentenceRecording) {
          console.debug('unexpected null sentenceRecordingMetadataWithPitchedAudios.sentenceRecording in fetchRecordingForDownload')
          return
        }
        if (!sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.file) {
          console.debug('unexpected null sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.file in fetchRecordingForDownload')
          return
        }
        if (!sentenceRecordingMetadataWithPitchedAudios.audio[state.pitchShift].sentenceHTMLAudioWrapper.audio) {
          console.debug(
            'unexpected null sentenceRecordingMetadataWithPitchedAudios.audio[state.pitchShift].sentenceHTMLAudioWrapper.audio in fetchRecordingForDownload',
          )
          return
        }
        link.href = sentenceRecordingMetadataWithPitchedAudios.audio[state.pitchShift].sentenceHTMLAudioWrapper.audio!.src
        link.setAttribute('download', sentenceRecordingMetadataWithPitchedAudios.sentenceRecording.file)
        link.click()
      })
      .catch((error) => {
        console.error('fetchRecordingForDownload failed:', error)
      })
      .finally(() => {
        state.downloadingSentenceRecordingId = null
      })
  }

  return {
    downloadingSentenceRecordingId: computed(() => state.downloadingSentenceRecordingId),
    fetchRecordingForDownload,
    isPlayable,
    pause,
    paused,
    pitchShift: computed({
      get: () => state.pitchShift,
      set: (v: PitchShiftType) => {
        state.pitchShift = v
      },
    }) as WritableComputedRef<PitchShiftType>,
    playSegment,
    playSentenceOrSequencedSegments,
    playing,
    playingSentenceRecordingId: computed(() => state.playingSentenceRecordingId),
    purge,
    resume,
    setLoadPlay,
  }
}
